/**
 * LeaguePublicSitePage — /league/:leagueId/public-site
 * ─────────────────────────────────────────────────────────────────
 * Public pages overhaul P4 (2026-06-09). The authoring surface
 * behind the redesigned public site:
 *
 *   • League news — the manager's public blog (league_posts,
 *     team_id NULL). manage_public_page (managers implicitly).
 *   • Team pages — posts on a team's public page. League editors
 *     can post to any enrolled team; a team's coach/manager can
 *     post to THEIR team (they reach this page via the "Team news"
 *     pill on their team chat page — coaches usually aren't league
 *     members, so the league sidebar never shows for them).
 *   • Coaches — curated public coach profiles (photo upload).
 *   • Sponsors — the sponsor strip above the public footer: name,
 *     website link, optional logo upload, order, show/hide
 *     (2026-09-28; routes/league-sponsors.js).
 *   • Settings — public-standings GF/GA toggle + public page link.
 *
 * Every write is re-checked server-side (league-posts.js /
 * league-coach-profiles / leagues PATCH); the tabs here are UX, not
 * security.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Newspaper, Users2, Image as ImageIcon, Settings2, Trash2, Pin, Eye, EyeOff, Plus, ExternalLink, Handshake, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api.js';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
import LeagueSubNav from './LeagueSubNav.jsx';
import MarkdownEditor from '../../components/MarkdownEditor.jsx';

export default function LeaguePublicSitePage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const leagueEditor = isManager || can('manage_public_page');

  // League meta (slug for "view public page" links + GF/GA flag).
  const [league, setLeague] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/leagues/${leagueId}`)
      .then(({ data }) => { if (!cancelled) setLeague(data?.league || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leagueId]);

  // Coached teams (chat-context probe — works for non-member coaches).
  const [myTeams, setMyTeams] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get(`/leagues/${leagueId}/chat/context`)
      .then(({ data }) => { if (!cancelled) setMyTeams(data?.my_teams || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leagueId]);

  // All enrolled teams (league editors can post to any team page).
  const [allTeams, setAllTeams] = useState([]);
  useEffect(() => {
    if (!league?.slug || !leagueEditor) return;
    let cancelled = false;
    api.get(`/public/leagues/${league.slug}/teams`)
      .then(({ data }) => { if (!cancelled) setAllTeams(data?.teams || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [league?.slug, leagueEditor]);

  const teamChoices = useMemo(() => {
    if (leagueEditor && allTeams.length) {
      return allTeams.map(t => ({ team_id: t.team_id, team_name: t.team_name }));
    }
    return myTeams;
  }, [leagueEditor, allTeams, myTeams]);

  const canTeamPost = teamChoices.length > 0;
  const [tab, setTab] = useState(null);
  useEffect(() => {
    if (tab) return;
    if (leagueEditor) setTab('news');
    else if (canTeamPost) setTab('team');
  }, [leagueEditor, canTeamPost, tab]);

  if (roleLoading) {
    return <div className="container"><LeagueSubNav league={null} /><div className="card">Loading…</div></div>;
  }
  if (!leagueEditor && !canTeamPost) {
    return (
      <div className="container">
        <LeagueSubNav league={null} />
        <div className="card">
          Public-site authoring is for league managers (or administrators with the
          public-page permission) and team coaches.
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingBottom: 60 }}>
      <LeagueSubNav league={null} />

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Newspaper size={20} color="var(--ice)" />
          <h1 className="section-title" style={{ margin: 0, flex: 1 }}>Public site</h1>
          {league?.slug && (
            <a href={`/l/${league.slug}`} target="_blank" rel="noopener noreferrer"
               className="btn-ghost" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ExternalLink size={13} /> View public page
            </a>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
          {leagueEditor && <TabBtn active={tab === 'news'} onClick={() => setTab('news')} icon={Newspaper}>League news</TabBtn>}
          {canTeamPost && <TabBtn active={tab === 'team'} onClick={() => setTab('team')} icon={Users2}>Team pages</TabBtn>}
          {leagueEditor && <TabBtn active={tab === 'coaches'} onClick={() => setTab('coaches')} icon={Users2}>Coaches</TabBtn>}
          {leagueEditor && <TabBtn active={tab === 'sponsors'} onClick={() => setTab('sponsors')} icon={Handshake}>Sponsors</TabBtn>}
          {leagueEditor && <TabBtn active={tab === 'settings'} onClick={() => setTab('settings')} icon={Settings2}>Settings</TabBtn>}
        </div>
      </div>

      {tab === 'news' && leagueEditor && (
        <PostsManager leagueId={leagueId} teamId={null} heading="League news"
          hint="Articles posted here appear on the public landing page and the News page." canPin />
      )}
      {tab === 'team' && canTeamPost && (
        <TeamPostsManager leagueId={leagueId} teams={teamChoices} canPin={leagueEditor} />
      )}
      {tab === 'coaches' && leagueEditor && <CoachProfilesManager leagueId={leagueId} teams={allTeams} />}
      {tab === 'sponsors' && leagueEditor && <SponsorsManager leagueId={leagueId} slug={league?.slug} />}
      {tab === 'settings' && leagueEditor && <PublicSettings league={league} onChanged={setLeague} />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
      border: `1px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
      background: active ? 'var(--ice-a10)' : 'var(--surface-2)',
      color: active ? 'var(--text)' : 'var(--text-2)',
      fontWeight: 700, fontSize: 13,
    }}>
      <Icon size={14} /> {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Team posts — team picker wrapping the shared PostsManager
// ─────────────────────────────────────────────────────────────────
function TeamPostsManager({ leagueId, teams, canPin }) {
  const [teamId, setTeamId] = useState(teams[0]?.team_id || '');
  useEffect(() => {
    if (!teamId && teams[0]) setTeamId(teams[0].team_id);
  }, [teams, teamId]);
  const team = teams.find(t => t.team_id === teamId);
  return (
    <>
      {teams.length > 1 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Team page</label>
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)}
            style={{ ...inputStyle, maxWidth: 280 }}>
            {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.team_name}</option>)}
          </select>
        </div>
      )}
      {teamId && (
        <PostsManager key={teamId} leagueId={leagueId} teamId={teamId}
          heading={`${team?.team_name || 'Team'} — page updates`}
          hint="Posts appear on this team's public page. Add a photo to make it pop."
          canPin={canPin} />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// PostsManager — list + composer for one scope (league or a team)
// ─────────────────────────────────────────────────────────────────
function PostsManager({ leagueId, teamId, heading, hint, canPin }) {
  const base = `/leagues/${leagueId}/posts`;
  const scopeQS = teamId ? `?team_id=${teamId}` : '';
  const [posts, setPosts] = useState(null);
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const { data } = await api.get(`${base}${scopeQS}`);
      setPosts(data?.posts || []);
    } catch (err) {
      setPosts([]);
      if (err?.response?.status === 403) toast.error('Not authorized for this page');
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId, teamId]);

  async function act(post, fn) {
    setBusyId(post.id);
    try { await fn(); load(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Action failed'); }
    finally { setBusyId(null); }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 className="section-title" style={{ fontSize: 16, margin: 0, flex: 1 }}>{heading}</h2>
        <button className="btn-ice" style={{ fontSize: 12 }} onClick={() => { setEditing(null); setComposing(true); }}>
          <Plus size={13} /> New post
        </button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 14 }}>{hint}</p>

      {composing && (
        <PostComposer key={editing?.id || '__new__'}
          leagueId={leagueId} teamId={teamId} existing={editing} canPin={canPin}
          onClose={() => { setComposing(false); setEditing(null); }}
          onSaved={() => { setComposing(false); setEditing(null); load(); }} />
      )}

      {posts === null && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Loading…</div>}
      {posts?.length === 0 && !composing && (
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Nothing posted yet.</div>
      )}
      {posts?.map(p => (
        <div key={p.id} style={{
          display: 'flex', gap: 12, alignItems: 'flex-start',
          padding: '12px 0', borderTop: '1px solid var(--border)',
        }}>
          {p.image_url && (
            <img src={p.image_url} alt="" style={{ width: 72, height: 52, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {p.title}
              {p.pinned && <span style={{ fontSize: 10, color: 'var(--puck)' }}>📌 pinned</span>}
              {!p.is_published && <span style={{ fontSize: 10, color: 'var(--text-2)' }}>draft</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
              {new Date(p.created_at).toLocaleDateString()}{p.author_name ? ` · ${p.author_name}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button className="btn-ghost" style={miniBtn} disabled={busyId === p.id}
              title={p.is_published ? 'Unpublish' : 'Publish'}
              onClick={() => act(p, () => api.patch(`${base}/${p.id}`, { is_published: !p.is_published }))}>
              {p.is_published ? <Eye size={13} /> : <EyeOff size={13} />}
            </button>
            {canPin && (
              <button className="btn-ghost" style={miniBtn} disabled={busyId === p.id}
                title={p.pinned ? 'Unpin' : 'Pin to top'}
                onClick={() => act(p, () => api.patch(`${base}/${p.id}`, { pinned: !p.pinned }))}>
                <Pin size={13} />
              </button>
            )}
            <button className="btn-ghost" style={miniBtn} onClick={() => { setEditing(p); setComposing(true); }}>
              Edit
            </button>
            <button className="btn-ghost" style={{ ...miniBtn, color: 'var(--penalty, #ff4444)' }} disabled={busyId === p.id}
              onClick={() => {
                if (!window.confirm(`Delete "${p.title}"?`)) return;
                act(p, () => api.delete(`${base}/${p.id}`));
              }}>
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PostComposer — create/edit + picture upload
// ─────────────────────────────────────────────────────────────────
function PostComposer({ leagueId, teamId, existing, canPin, onClose, onSaved }) {
  const base = `/leagues/${leagueId}/posts`;
  const [title, setTitle] = useState(existing?.title || '');
  const [body, setBody] = useState(existing?.body_markdown || '');
  const [pinned, setPinned] = useState(existing?.pinned || false);
  const [imageFile, setImageFile] = useState(null);   // pending upload
  const [imagePreview, setImagePreview] = useState(existing?.image_url || null);
  const [removeImage, setRemoveImage] = useState(false);
  const [saving, setSaving] = useState(false);

  function pickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      return toast.error('Image must be PNG, JPEG, or WebP');
    }
    if (file.size > 4 * 1024 * 1024) return toast.error('Image exceeds the 4 MB cap');
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  }

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  async function save() {
    if (!title.trim()) return toast.error('Give the post a title');
    setSaving(true);
    try {
      let postId = existing?.id;
      if (postId) {
        await api.patch(`${base}/${postId}`, {
          title: title.trim(), body_markdown: body || null,
          ...(canPin ? { pinned } : {}),
        });
      } else {
        const { data } = await api.post(base, {
          title: title.trim(), body_markdown: body || null,
          team_id: teamId || null,
          ...(canPin ? { pinned } : {}),
        });
        postId = data?.post?.id;
      }
      if (postId && imageFile) {
        const dataUrl = await fileToDataUrl(imageFile);
        await api.post(`${base}/${postId}/image`, { data: dataUrl, mimeType: imageFile.type });
      } else if (postId && removeImage && existing?.image_url) {
        await api.delete(`${base}/${postId}/image`);
      }
      toast.success(existing ? 'Post updated' : 'Posted to the public page');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save post');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ border: '1px dashed var(--border)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <label style={labelStyle}>Title</label>
      <input type="text" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. 'Championship Saturday — what you need to know'"
        style={{ ...inputStyle, marginBottom: 12 }} />

      <label style={labelStyle}>Body (markdown supported)</label>
      <MarkdownEditor value={body} onChange={setBody} rows={8} />

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
        <label className="btn-ghost" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <ImageIcon size={14} /> {imagePreview ? 'Replace picture' : 'Add a picture'}
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={pickImage} style={{ display: 'none' }} />
        </label>
        {imagePreview && (
          <>
            <img src={imagePreview} alt="" style={{ height: 48, borderRadius: 8 }} />
            <button type="button" className="btn-ghost" style={{ fontSize: 11 }}
              onClick={() => { setImageFile(null); setImagePreview(null); setRemoveImage(true); }}>
              Remove picture
            </button>
          </>
        )}
        {canPin && (
          <label style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
            Pin to top
          </label>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn-ice" onClick={save} disabled={saving || !title.trim()}>
          {saving ? 'Saving…' : existing ? 'Save changes' : 'Publish post'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Coach profiles editor
// ─────────────────────────────────────────────────────────────────
function CoachProfilesManager({ leagueId, teams }) {
  const base = `/leagues/${leagueId}/coach-profiles`;
  const [profiles, setProfiles] = useState(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('Head coach');
  const [bio, setBio] = useState('');
  const [teamId, setTeamId] = useState('');
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(base);
      setProfiles(data?.profiles || []);
    } catch { setProfiles([]); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId]);

  async function create() {
    if (!name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await api.post(base, {
        display_name: name.trim(), role: role.trim() || 'Coach',
        bio: bio.trim() || null, team_id: teamId || null,
      });
      setName(''); setBio(''); setAdding(false);
      toast.success('Profile added');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not add profile');
    } finally { setSaving(false); }
  }

  async function uploadPhoto(profile, file) {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      return toast.error('Photo must be PNG, JPEG, or WebP');
    }
    if (file.size > 4 * 1024 * 1024) return toast.error('Photo exceeds the 4 MB cap');
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`${base}/${profile.id}/photo`, { data: reader.result, mimeType: file.type });
        toast.success('Photo updated');
        load();
      } catch (err) {
        toast.error(err?.response?.data?.error || 'Upload failed');
      }
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <h2 className="section-title" style={{ fontSize: 16, margin: 0, flex: 1 }}>Public coach profiles</h2>
        <button className="btn-ice" style={{ fontSize: 12 }} onClick={() => setAdding(a => !a)}>
          <Plus size={13} /> Add coach
        </button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 14 }}>
        Only people you add here appear on the public Coaches page — publishing someone's
        name is your editorial call, never automatic.
      </p>

      {adding && (
        <div style={{ border: '1px dashed var(--border)', borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ flex: '1 1 180px' }}>
              <label style={labelStyle}>Name</label>
              <input type="text" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label style={labelStyle}>Role</label>
              <input type="text" value={role} maxLength={60} onChange={(e) => setRole(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelStyle}>Team (optional)</label>
              <select value={teamId} onChange={(e) => setTeamId(e.target.value)} style={inputStyle}>
                <option value="">—</option>
                {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.team_name}</option>)}
              </select>
            </div>
          </div>
          <label style={labelStyle}>Short bio (optional)</label>
          <textarea value={bio} maxLength={5000} rows={3} onChange={(e) => setBio(e.target.value)}
            style={{ ...inputStyle, resize: 'vertical' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
            <button className="btn-ghost" onClick={() => setAdding(false)} disabled={saving}>Cancel</button>
            <button className="btn-ice" onClick={create} disabled={saving || !name.trim()}>
              {saving ? 'Adding…' : 'Add profile'}
            </button>
          </div>
        </div>
      )}

      {profiles === null && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Loading…</div>}
      {profiles?.length === 0 && !adding && (
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>No profiles yet.</div>
      )}
      {profiles?.map(p => (
        <div key={p.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--border)' }}>
          {p.photo_url
            ? <img src={p.photo_url} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />
            : <span style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--surface-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>{p.display_name.slice(0, 1)}</span>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              {p.display_name}
              {!p.is_active && <span style={{ fontSize: 10, color: 'var(--text-2)', marginLeft: 8 }}>hidden</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{p.role}{p.team_name ? ` · ${p.team_name}` : ''}</div>
          </div>
          <label className="btn-ghost" style={{ ...miniBtn, cursor: 'pointer' }} title="Upload photo">
            <ImageIcon size={13} />
            <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(p, f); }} />
          </label>
          <button className="btn-ghost" style={miniBtn}
            onClick={async () => {
              try {
                await api.patch(`${base}/${p.id}`, { is_active: !p.is_active });
                load();
              } catch (err) { toast.error(err?.response?.data?.error || 'Failed'); }
            }}>
            {p.is_active ? <Eye size={13} /> : <EyeOff size={13} />}
          </button>
          <button className="btn-ghost" style={{ ...miniBtn, color: 'var(--penalty, #ff4444)' }}
            onClick={async () => {
              if (!window.confirm(`Remove ${p.display_name} from the public page?`)) return;
              try { await api.delete(`${base}/${p.id}`); load(); }
              catch (err) { toast.error(err?.response?.data?.error || 'Failed'); }
            }}>
            <Trash2 size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Sponsors — the strip above the public footer (2026-09-28)
// ─────────────────────────────────────────────────────────────────
function SponsorsManager({ leagueId, slug }) {
  const base = `/leagues/${leagueId}/sponsors`;
  const [sponsors, setSponsors] = useState(null);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);   // { id, name, external_url }

  async function load() {
    try {
      const { data } = await api.get(base);
      setSponsors(data?.sponsors || []);
    } catch (err) {
      setSponsors([]);
      if (err?.response?.status !== 403) toast.error(err?.response?.data?.error || 'Could not load sponsors');
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId]);

  async function add() {
    if (!name.trim()) return toast.error('Sponsor name is required');
    setSaving(true);
    try {
      await api.post(base, { name: name.trim(), external_url: url.trim() || null });
      setName(''); setUrl('');
      toast.success('Sponsor added');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not add sponsor');
    } finally { setSaving(false); }
  }

  async function saveEdit() {
    if (!editing) return;
    try {
      await api.patch(`${base}/${editing.id}`, { name: editing.name, external_url: editing.external_url || null });
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || 'Could not save'); }
  }

  async function move(idx, dir) {
    const list = [...(sponsors || [])];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    setSponsors(list);
    try {
      const { data } = await api.post(`${base}/reorder`, { ids: list.map((x) => x.id) });
      setSponsors(data?.sponsors || list);
    } catch (err) { toast.error('Could not reorder'); load(); }
  }

  function uploadLogo(sp, file) {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      return toast.error('Logo must be PNG, JPEG, or WebP');
    }
    if (file.size > 4 * 1024 * 1024) return toast.error('Logo exceeds the 4 MB cap');
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`${base}/${sp.id}/logo`, { data: reader.result, mimeType: file.type });
        toast.success('Logo updated');
        load();
      } catch (err) { toast.error(err?.response?.data?.error || 'Upload failed'); }
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 className="section-title" style={{ fontSize: 16, margin: 0, flex: 1 }}>Sponsors</h2>
        {slug && (
          <a href={`/l/${slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost"
             style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ExternalLink size={13} /> See it on the site
          </a>
        )}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 14 }}>
        Sponsors appear in a strip above the footer on every page of your public site, in this order.
        Add a logo (PNG, JPEG or WebP, up to 4 MB) or leave it as a name. Hidden sponsors stay here but
        don&apos;t show publicly.
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <div style={{ flex: '1 1 180px' }}>
          <label style={labelStyle}>Sponsor name</label>
          <input type="text" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} style={inputStyle}
                 placeholder="e.g. Riverside Sports Bar" onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
        </div>
        <div style={{ flex: '1 1 220px' }}>
          <label style={labelStyle}>Website (optional)</label>
          <input type="text" value={url} maxLength={500} onChange={(e) => setUrl(e.target.value)} style={inputStyle}
                 placeholder="example.com" onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
        </div>
        <button className="btn-ice" style={{ fontSize: 12 }} onClick={add} disabled={saving || !name.trim()}>
          <Plus size={13} /> {saving ? 'Adding…' : 'Add sponsor'}
        </button>
      </div>

      {sponsors === null && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Loading…</div>}
      {sponsors?.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>No sponsors yet.</div>}
      {sponsors?.map((sp, i) => (
        <div key={sp.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <div style={{ width: 84, height: 44, borderRadius: 8, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, border: '1px solid var(--border)' }}>
            {sp.logo_url
              ? <img src={sp.logo_url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <span style={{ fontSize: 10, color: '#64748b', padding: 4, textAlign: 'center' }}>no logo</span>}
          </div>
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            {editing?.id === sp.id ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <input type="text" value={editing.name} maxLength={120} style={{ ...inputStyle, flex: '1 1 140px' }}
                       onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                <input type="text" value={editing.external_url || ''} maxLength={500} style={{ ...inputStyle, flex: '1 1 180px' }}
                       placeholder="Website" onChange={(e) => setEditing({ ...editing, external_url: e.target.value })} />
                <button className="btn-ice" style={miniBtn} onClick={saveEdit}>Save</button>
                <button className="btn-ghost" style={miniBtn} onClick={() => setEditing(null)}>Cancel</button>
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {sp.name}
                  {!sp.is_active && <span style={{ fontSize: 10, color: 'var(--text-2)', marginLeft: 8 }}>hidden</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sp.external_url || 'No website link'}
                </div>
              </>
            )}
          </div>
          {editing?.id !== sp.id && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <button className="btn-ghost" style={miniBtn} title="Move up" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={13} /></button>
              <button className="btn-ghost" style={miniBtn} title="Move down" disabled={i === sponsors.length - 1} onClick={() => move(i, 1)}><ArrowDown size={13} /></button>
              <label className="btn-ghost" style={{ ...miniBtn, cursor: 'pointer' }} title="Upload logo">
                <ImageIcon size={13} />
                <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(sp, f); e.target.value = ''; }} />
              </label>
              <button className="btn-ghost" style={miniBtn} title="Edit" onClick={() => setEditing({ id: sp.id, name: sp.name, external_url: sp.external_url || '' })}>Edit</button>
              <button className="btn-ghost" style={miniBtn} title={sp.is_active ? 'Hide from the site' : 'Show on the site'}
                onClick={async () => {
                  try { await api.patch(`${base}/${sp.id}`, { is_active: !sp.is_active }); load(); }
                  catch (err) { toast.error(err?.response?.data?.error || 'Failed'); }
                }}>
                {sp.is_active ? <Eye size={13} /> : <EyeOff size={13} />}
              </button>
              <button className="btn-ghost" style={{ ...miniBtn, color: 'var(--penalty, #ff4444)' }} title="Remove"
                onClick={async () => {
                  if (!window.confirm(`Remove ${sp.name}?`)) return;
                  try { await api.delete(`${base}/${sp.id}`); load(); }
                  catch (err) { toast.error(err?.response?.data?.error || 'Failed'); }
                }}>
                <Trash2 size={13} />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Public settings — GF/GA toggle (+ pointer to LeagueSettingsPage)
// ─────────────────────────────────────────────────────────────────
function PublicSettings({ league, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!league) return <div className="card">Loading…</div>;
  const showGoals = league.public_show_goal_stats !== false;

  async function toggle() {
    setBusy(true);
    try {
      const { data } = await api.patch(`/leagues/${league.id}`, {
        public_show_goal_stats: !showGoals,
      });
      onChanged(prev => ({ ...prev, ...data?.league }));
      toast.success(!showGoals
        ? 'Public standings now show goals for/against'
        : 'Goals for/against hidden on public standings');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not update');
    } finally { setBusy(false); }
  }

  return (
    <div className="card">
      <h2 className="section-title" style={{ fontSize: 16, marginBottom: 12 }}>Public page settings</h2>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 14 }}>
        <input type="checkbox" checked={showGoals} disabled={busy} onChange={toggle} style={{ marginTop: 3 }} />
        <span style={{ fontSize: 14 }}>
          <strong>Show goals for / against on public standings</strong>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
            When off, the public standings show GP/W/L/OTL/T/PTS only. (Standings are
            still SORTED by goal differential either way.)
          </div>
        </span>
      </label>
      <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
        Hero tagline, about section, theme color, contact info and the public on/off
        switch live in League → Settings.
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 12, color: 'var(--text-2)', marginBottom: 4 };
const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--border)', background: 'var(--bg-2)',
  color: 'var(--text)', fontSize: 13, outline: 'none',
};
const miniBtn = { fontSize: 11, padding: '4px 8px' };
